/**
 * DSS search-area box on the main map (ENG-207). A square (spec D6) with
 * corner dots and side bars; drag the middle to move it, drag a handle to
 * resize. The drag previews locally and writes the store once on drop, so the
 * external-OI query refetches once per gesture.
 */
import { getMap } from '@/lib/map/instance';
import { VOLUME_PALETTE } from '@/lib/map/volumeStyle';
import {
  type HandleId,
  type LngLat,
  buildSearchAreaFC,
  sizeFromHandleDrag,
  squareBbox,
} from '@/lib/oi/searchArea';
import { type OiSearchArea, useOiStore } from '@/lib/store/oi';
import { useUiStore } from '@/lib/store/ui';
import type {
  GeoJSONSource,
  MapMouseEvent,
  MapTouchEvent,
  Map as MapboxMap,
  PointLike,
} from 'mapbox-gl';

const SRC = 'src-oi-search-area';
export const SEARCH_AREA_LAYERS = {
  fill: 'lyr-oi-search-fill',
  line: 'lyr-oi-search-line',
  side: 'lyr-oi-search-side',
  corner: 'lyr-oi-search-corner',
} as const;

const COLOR = VOLUME_PALETTE.green;
const EMPTY = { type: 'FeatureCollection' as const, features: [] };
/** Half-size of the handle hit box — touch gets a ~44 px target. */
const HIT_PX_MOUSE = 8;
const HIT_PX_TOUCH = 22;
/** Mapbox fires `click` after `mouseup`; ignore it this long after a drag. */
const CLICK_SUPPRESS_MS = 250;
/** Mapbox's own click tolerance — less travel than this is a click, not a drag. */
const DRAG_THRESHOLD_PX = 3;
/** Leaves room for the docked scrubber and the map's corner controls. */
const REVEAL_PADDING_PX = 64;

const CURSOR: Record<HandleId, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
};

let suppressClickUntil = 0;

/** True just after a search-area drag ends — the OI layer checks this so
 *  dropping the box on a polygon doesn't select that OI. */
export function searchAreaDragJustEnded(nowMs: number = performance.now()): boolean {
  return nowMs < suppressClickUntil;
}

export function searchAreaVisible(): boolean {
  const s = useOiStore.getState();
  return (
    useUiStore.getState().activeContext === 'oi' &&
    s.category === '3rdparty' &&
    s.searchArea.center !== null
  );
}

function ensureLayers(map: MapboxMap) {
  if (!map.getSource(SRC)) map.addSource(SRC, { type: 'geojson', data: EMPTY });
  if (!map.getLayer(SEARCH_AREA_LAYERS.fill)) {
    map.addLayer({
      id: SEARCH_AREA_LAYERS.fill,
      type: 'fill',
      source: SRC,
      filter: ['==', ['get', 'role'], 'area'],
      paint: { 'fill-color': COLOR, 'fill-opacity': 0.12 },
    });
  }
  if (!map.getLayer(SEARCH_AREA_LAYERS.line)) {
    map.addLayer({
      id: SEARCH_AREA_LAYERS.line,
      type: 'line',
      source: SRC,
      filter: ['==', ['get', 'role'], 'area'],
      paint: { 'line-color': COLOR, 'line-width': 1.5, 'line-dasharray': [2, 2] },
    });
  }
  if (!map.getLayer(SEARCH_AREA_LAYERS.side)) {
    map.addLayer({
      id: SEARCH_AREA_LAYERS.side,
      type: 'line',
      source: SRC,
      filter: ['==', ['get', 'role'], 'side'],
      layout: { 'line-cap': 'round' },
      paint: { 'line-color': COLOR, 'line-width': 6 },
    });
  }
  if (!map.getLayer(SEARCH_AREA_LAYERS.corner)) {
    map.addLayer({
      id: SEARCH_AREA_LAYERS.corner,
      type: 'circle',
      source: SRC,
      filter: ['==', ['get', 'role'], 'corner'],
      paint: {
        'circle-radius': 5,
        'circle-color': COLOR,
        'circle-stroke-color': 'white',
        'circle-stroke-width': 1.5,
      },
    });
  }
}

function render(map: MapboxMap, center: LngLat | null, sizeKm: number) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  src.setData(center ? buildSearchAreaFC(center, sizeKm) : EMPTY);
}

/** Fit the camera to the search box when any part of it is out of view —
 *  on entering 3rd Party and on Set Focus, so its border and handles are
 *  reachable. */
export function revealSearchArea(map: MapboxMap, area: OiSearchArea) {
  if (!area.center) return;
  const b = squareBbox(area.center, area.sizeKm);
  const view = map.getBounds();
  if (!view) return;
  const inView =
    view.getWest() <= b.swLng &&
    view.getEast() >= b.neLng &&
    view.getSouth() <= b.swLat &&
    view.getNorth() >= b.neLat;
  if (inView) return;
  map.fitBounds(
    [
      [b.swLng, b.swLat],
      [b.neLng, b.neLat],
    ],
    { padding: REVEAL_PADDING_PX, duration: 600 },
  );
}

/** `revealSearchArea` against the live map and the store's current area. */
export function revealCurrentSearchArea() {
  const map = getMap();
  if (map) revealSearchArea(map, useOiStore.getState().searchArea);
}

/** Screen distance within which the box counts as centred on the crosshair. */
const CROSSHAIR_TOLERANCE_PX = 4;

/** True when the box sits under the map-centre crosshair, i.e. Set Focus
 *  would not move it. Compared in screen space so it holds at any zoom. */
export function isSearchAreaUnderCrosshair(
  map: Pick<MapboxMap, 'project' | 'getCenter'>,
  center: LngLat | null,
): boolean {
  if (!center) return false;
  const box = map.project(center);
  const mid = map.project(map.getCenter());
  return Math.hypot(box.x - mid.x, box.y - mid.y) <= CROSSHAIR_TOLERANCE_PX;
}

type Drag =
  | { kind: 'move'; offset: LngLat; center: LngLat; sizeKm: number }
  | { kind: 'resize'; handle: HandleId; center: LngLat; sizeKm: number };

type Hit = { kind: 'move' } | { kind: 'resize'; handle: HandleId } | null;

export function subscribeOiSearchAreaLayer(map: MapboxMap): () => void {
  ensureLayers(map);

  let drag: Drag | null = null;
  let moved = false;
  let pending: LngLat | null = null;
  let rafId: number | null = null;
  let downPoint = { x: 0, y: 0 };
  let hoverPoint: { x: number; y: number } | null = null;
  let hoverRafId: number | null = null;
  let cursorOwned = false;

  const sync = () => {
    if (drag) return;
    const { searchArea } = useOiStore.getState();
    render(map, searchAreaVisible() ? searchArea.center : null, searchArea.sizeKm);
  };

  const hitTest = (point: { x: number; y: number }, touch: boolean): Hit => {
    const r = touch ? HIT_PX_TOUCH : HIT_PX_MOUSE;
    const box: [PointLike, PointLike] = [
      [point.x - r, point.y - r],
      [point.x + r, point.y + r],
    ];
    const handle = map.queryRenderedFeatures(box, {
      layers: [SEARCH_AREA_LAYERS.corner, SEARCH_AREA_LAYERS.side],
    })[0]?.properties?.handle;
    if (typeof handle === 'string') return { kind: 'resize', handle: handle as HandleId };
    // Move only from the border band: the default box is usually bigger than
    // the view, so its inside has to keep panning the map.
    const onEdge = map.queryRenderedFeatures(box, { layers: [SEARCH_AREA_LAYERS.line] }).length > 0;
    return onEdge ? { kind: 'move' } : null;
  };

  const applyPreview = (p: LngLat) => {
    const d = drag;
    if (!d) return;
    if (d.kind === 'move') d.center = [p[0] + d.offset[0], p[1] + d.offset[1]];
    else d.sizeKm = sizeFromHandleDrag(d.center, p, d.handle);
    render(map, d.center, d.sizeKm);
  };

  const onMove = (e: MapMouseEvent | MapTouchEvent) => {
    if (!drag) return;
    if (!moved) {
      const dist = Math.hypot(e.point.x - downPoint.x, e.point.y - downPoint.y);
      if (dist < DRAG_THRESHOLD_PX) return;
      moved = true;
    }
    pending = [e.lngLat.lng, e.lngLat.lat];
    if (rafId === null) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (pending) applyPreview(pending);
      });
    }
  };

  const onEnd = () => {
    map.off('mousemove', onMove);
    map.off('touchmove', onMove);
    map.off('mouseup', onEnd);
    map.off('touchend', onEnd);
    map.off('touchcancel', onEnd);
    window.removeEventListener('mouseup', onEnd);
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (pending) applyPreview(pending);
    const d = drag;
    drag = null;
    pending = null;
    map.dragPan.enable();
    if (!d || !moved) return;
    suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS;
    useOiStore
      .getState()
      .setSearchArea(d.kind === 'move' ? { center: d.center } : { sizeKm: d.sizeKm });
  };

  /** Abandon the gesture and put the box back where the store has it. */
  const cancelDrag = () => {
    moved = false;
    pending = null;
    onEnd();
    sync();
  };

  const onDown = (e: MapMouseEvent | MapTouchEvent) => {
    // Two-finger gestures are pinch-zoom, not a box drag — a second finger
    // mid-drag hands the gesture back to the map.
    if ('points' in e && Array.isArray(e.points) && e.points.length > 1) {
      if (drag) cancelDrag();
      return;
    }
    if (!searchAreaVisible()) return;
    const { center, sizeKm } = useOiStore.getState().searchArea;
    if (!center) return;
    const hit = hitTest(e.point, e.type === 'touchstart');
    if (!hit) return;
    e.preventDefault();
    map.dragPan.disable();
    drag =
      hit.kind === 'move'
        ? {
            kind: 'move',
            offset: [center[0] - e.lngLat.lng, center[1] - e.lngLat.lat],
            center,
            sizeKm,
          }
        : { kind: 'resize', handle: hit.handle, center, sizeKm };
    moved = false;
    downPoint = { x: e.point.x, y: e.point.y };
    map.on('mousemove', onMove);
    map.on('touchmove', onMove);
    map.on('mouseup', onEnd);
    map.on('touchend', onEnd);
    map.on('touchcancel', onEnd);
    // A release outside the canvas never reaches the map.
    window.addEventListener('mouseup', onEnd);
  };

  const updateCursor = () => {
    hoverRafId = null;
    const point = hoverPoint;
    if (drag || !point) return;
    const hit = searchAreaVisible() ? hitTest(point, false) : null;
    const canvas = map.getCanvas();
    if (hit) {
      canvas.style.cursor = hit.kind === 'move' ? 'move' : CURSOR[hit.handle];
      cursorOwned = true;
    } else if (cursorOwned) {
      canvas.style.cursor = '';
      cursorOwned = false;
    }
  };

  // Hover hit-testing is coalesced to one query per frame — raw mousemove can
  // fire faster than the display refreshes.
  const onHover = (e: MapMouseEvent) => {
    if (drag) return;
    hoverPoint = { x: e.point.x, y: e.point.y };
    if (hoverRafId === null) hoverRafId = requestAnimationFrame(updateCursor);
  };

  sync();
  const unsubOi = useOiStore.subscribe(
    (s) => `${s.category}|${s.searchArea.center?.join(',') ?? ''}|${s.searchArea.sizeKm}`,
    sync,
  );
  const unsubUi = useUiStore.subscribe((s) => s.activeContext, sync);
  map.on('mousedown', onDown);
  map.on('touchstart', onDown);
  map.on('mousemove', onHover);

  return () => {
    unsubOi();
    unsubUi();
    map.off('mousedown', onDown);
    map.off('touchstart', onDown);
    map.off('mousemove', onHover);
    if (hoverRafId !== null) cancelAnimationFrame(hoverRafId);
    if (drag) onEnd();
  };
}
